/**
 * Solar position and daylight phases for the real-sun day/night cycle (spec §6.10).
 *
 * The elevation formula follows the NOAA solar calculator (accuracy well under 1°, which is
 * plenty for lighting). It is implemented here instead of pulling in a dependency so that
 * client and server share one tiny, fully tested implementation.
 */

const DEG = Math.PI / 180;

export interface GeoLocation {
  /** Degrees, north positive. */
  readonly latitude: number;
  /** Degrees, east positive. */
  readonly longitude: number;
}

/** The Vermiculus office in Umeå, Sweden. */
export const UMEA: GeoLocation = { latitude: 63.82842, longitude: 20.25972 };

/** Solar elevation above the horizon in degrees (negative below the horizon). */
export function solarElevation(date: Date, location: GeoLocation): number {
  const julianDay = date.getTime() / 86_400_000 + 2_440_587.5;
  const t = (julianDay - 2_451_545) / 36_525; // Julian centuries since J2000.0

  const meanLongitude = normalizeDegrees(280.46646 + t * (36_000.76983 + t * 0.0003032));
  const meanAnomaly = 357.52911 + t * (35_999.05029 - 0.0001537 * t);
  const eccentricity = 0.016708634 - t * (0.000042037 + 0.0000001267 * t);
  const center =
    Math.sin(meanAnomaly * DEG) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(2 * meanAnomaly * DEG) * (0.019993 - 0.000101 * t) +
    Math.sin(3 * meanAnomaly * DEG) * 0.000289;
  const trueLongitude = meanLongitude + center;
  const omega = 125.04 - 1934.136 * t;
  const apparentLongitude = trueLongitude - 0.00569 - 0.00478 * Math.sin(omega * DEG);
  const meanObliquity =
    23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60;
  const obliquity = meanObliquity + 0.00256 * Math.cos(omega * DEG);
  const declination = Math.asin(Math.sin(obliquity * DEG) * Math.sin(apparentLongitude * DEG));

  const y = Math.tan((obliquity / 2) * DEG) ** 2;
  const equationOfTimeMinutes =
    (4 / DEG) *
    (y * Math.sin(2 * meanLongitude * DEG) -
      2 * eccentricity * Math.sin(meanAnomaly * DEG) +
      4 * eccentricity * y * Math.sin(meanAnomaly * DEG) * Math.cos(2 * meanLongitude * DEG) -
      0.5 * y * y * Math.sin(4 * meanLongitude * DEG) -
      1.25 * eccentricity * eccentricity * Math.sin(2 * meanAnomaly * DEG));

  const utcMinutes = date.getUTCHours() * 60 + date.getUTCMinutes() + date.getUTCSeconds() / 60;
  const trueSolarMinutes = mod(utcMinutes + equationOfTimeMinutes + 4 * location.longitude, 1440);
  const hourAngle = trueSolarMinutes / 4 - 180;

  const latitude = location.latitude * DEG;
  const cosZenith =
    Math.sin(latitude) * Math.sin(declination) +
    Math.cos(latitude) * Math.cos(declination) * Math.cos(hourAngle * DEG);
  const zenith = Math.acos(clamp(cosZenith, -1, 1)) / DEG;
  return 90 - zenith;
}

export type LightPhase = 'day' | 'golden-hour' | 'civil-twilight' | 'nautical-twilight' | 'night';

/** Lighting phase by solar elevation (spec §6.10 table). */
export function lightPhase(elevationDegrees: number): LightPhase {
  if (elevationDegrees > 6) return 'day';
  if (elevationDegrees > 0) return 'golden-hour';
  if (elevationDegrees > -6) return 'civil-twilight';
  if (elevationDegrees > -12) return 'nautical-twilight';
  return 'night';
}

/**
 * Smooth outdoor daylight factor: 0 at full night (≤ −12°), 1 at full day (≥ 6°),
 * smoothstep in between so lighting never jumps between phases.
 */
export function daylightFactor(elevationDegrees: number): number {
  const x = clamp((elevationDegrees + 12) / 18, 0, 1);
  return x * x * (3 - 2 * x);
}

function normalizeDegrees(value: number): number {
  return mod(value, 360);
}

function mod(value: number, modulus: number): number {
  return ((value % modulus) + modulus) % modulus;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
